import type { Card } from '@klopotun/contracts';
import type { CardBodyDraft, CardFormValues } from '../model/card-form-values.js';

const WHOLE_NUMBER_PATTERN = /^\d+$/;

const toWholeNumber = (value: string): number | null => {
  const trimmed = value.trim();

  if (!WHOLE_NUMBER_PATTERN.test(trimmed)) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);

  return Number.isSafeInteger(parsed) ? parsed : null;
};

/**
 * An expiry field is allowed to stay empty, so "not a number" cannot be reported as a missing
 * value: without this guard a typo in the month would quietly save as no expiry at all.
 */
export const isBlankOrWholeNumber = (value: string): boolean => {
  const trimmed = value.trim();

  return trimmed === '' || WHOLE_NUMBER_PATTERN.test(trimmed);
};

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

export const emptyCardFormValues = (baseCurrency: string): CardFormValues => ({
  label: '',
  last4: '',
  brand: '',
  expiresMonth: '',
  expiresYear: '',
  currency: baseCurrency,
  color: '',
});

export const toCardFormValues = (card: Card): CardFormValues => ({
  label: card.label,
  last4: card.last4 ?? '',
  brand: card.brand ?? '',
  expiresMonth: card.expiresMonth === null ? '' : String(card.expiresMonth),
  expiresYear: card.expiresYear === null ? '' : String(card.expiresYear),
  currency: card.currency ?? '',
  color: card.color ?? '',
});

export const toCardBodyDraft = (values: CardFormValues): CardBodyDraft => ({
  label: values.label.trim(),
  last4: emptyToNull(values.last4),
  brand: emptyToNull(values.brand),
  expiresMonth: toWholeNumber(values.expiresMonth),
  expiresYear: toWholeNumber(values.expiresYear),
  currency: emptyToNull(values.currency),
  color: emptyToNull(values.color),
});
